// Material Dashboard 2 PRO React examples
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";
import Grid from "@mui/material/Grid2";
import MDBox from "components/MDBox";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getTrends,
  setLoading,
  saveTrendData,
  clearAdded,
} from "store/actions/uniqueExperienceActions";
import { useSearchParams } from "react-router";
import columnsMap from "components/MaterialTablePicker/TableColumns";
import DynamicTabs from "components/DynamicTabs";
import DynamicRadioGroup from "components/DynamicRadioGroup";
import DestinationPicker from "components/AsyncSelect/DestinationPicker";
import LocationPickerAsync from "components/AutoComplete/LocationsPickerAsync";
import { parseISO } from "date-fns";
import UniqueExperiencesList from "./components/UniqueExperiencesList";

// adding a detailTabs
const detailTabs = [
  { id: 1, title: "Destination" },
  { id: 2, title: "Location" },
];

const initialSectionOptionsData = [
  { value: "unique-experiences", label: "Unique experiences", disable: false },
  { value: "recommended-trip", label: "Recommended Trip Plans", disable: false },
];

const AllUniqueExperiences = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { trends: uniqueExperiences, loading, added } = useSelector((s) => s.trends);

  // State
  const [uniqueExperiencesList, setUniqueExperiencesList] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [sectionType, setSectionType] = useState("unique-experiences");
  const [sectionOptions, setSectionOptions] = useState(initialSectionOptionsData);
  const [selectedDestination, setSelectedDestination] = useState({});
  const [selectedLocation, setSelectedLocation] = useState({});

  const data = {
    title: "Unique Experiences",
    columns: [
      columnsMap.titleCol,
      columnsMap.type,
      columnsMap.trendingStart,
      columnsMap.trendingEnd,
      // "Approval Status",
    ],
    screen: "Unique Experiences",
  };

  const handleDestinationChange = (item) => {
    if (item?.value) {
      dispatch(setLoading());
      setSelectedDestination(item);
      setSelectedLocation({});
    } else {
      // setSelectedDestination({});
      setUniqueExperiencesList([]);
      setSectionOptions(initialSectionOptionsData.map((opt) => ({ ...opt, disable: false })));
    }
  };

  const handleLocationChange = (item) => {
    if (item?.value) {
      dispatch(setLoading());
      setSelectedLocation(item);
    } else {
      setSelectedLocation({});
      setUniqueExperiencesList([]);
      setSectionOptions(initialSectionOptionsData.map((opt) => ({ ...opt, disable: false })));
    }
  };

  const handleSectionChange = (_, value) => {
    const currentSelectedOptionConfig = sectionOptions.find((opt) => opt.value === value);
    if (currentSelectedOptionConfig && currentSelectedOptionConfig.disable) {
      return;
    }

    if (sectionType !== value) {
      setSectionType(value);
    }
  };

  useEffect(() => {
    const itemId = searchParams.get("itemId");
    const itemLabel = searchParams.get("itemLabel");
    const isTripParam = searchParams.get("isTrip") === "true";
    const isDestination = searchParams.get("isDestination") === "true";

    if (itemId && itemLabel) {
      if (isDestination) {
        setSelectedDestination({ label: itemLabel, value: itemId });
      } else {
        setSelectedLocation({ label: itemLabel, value: itemId });
      }
      setTabValue(isDestination ? 0 : 1);
      setSectionType(isTripParam ? "recommended-trip" : "unique-experiences");
    }
  }, [searchParams]);

  useEffect(() => {
    const activeItem = tabValue === 0 ? selectedDestination : selectedLocation;
    const isDest = tabValue === 0;

    if (activeItem?.value) {
      dispatch(setLoading());
      dispatch(saveTrendData(activeItem, isDest, sectionType === "recommended-trip"));
      dispatch(getTrends(activeItem, isDest, sectionType === "recommended-trip"));
    } else {
      // clear local list & options if no filter
      setSectionOptions(initialSectionOptionsData.map((opt) => ({ ...opt, disable: false })));
    }
  }, [selectedDestination, selectedLocation, sectionType]);

  useEffect(() => {
    if (added) {
      const activeItem = tabValue === 0 ? selectedDestination : selectedLocation;
      const isDest = tabValue === 0;
      dispatch(setLoading());
      dispatch(getTrends(activeItem, isDest, sectionType === "recommended-trip"));
      dispatch(clearAdded());
    }
  }, [added, tabValue, selectedDestination, selectedLocation, sectionType]);

  useEffect(() => {
    let newSectionOptions = initialSectionOptionsData.map((opt) => ({ ...opt, disable: false }));
    let currentSectionType = sectionType;

    const isActiveSelection =
      (tabValue === 0 && selectedDestination?.value) || (tabValue === 1 && selectedLocation?.value);

    if (uniqueExperiences && uniqueExperiences.length > 0 && isActiveSelection) {
      const modifiedUniqueExperiences = uniqueExperiences.map((experience) => {
        const trendingEndDate = parseISO(experience.trendingEnd);
        trendingEndDate.setDate(trendingEndDate.getDate() - 1);
        return { ...experience, trendingEnd: trendingEndDate };
      });

      setUniqueExperiencesList(modifiedUniqueExperiences);

      const firstExperienceType = uniqueExperiences[0]?.type;

      if (firstExperienceType === "ticket" || firstExperienceType === "recommendation") {
        currentSectionType = "unique-experiences";
        newSectionOptions = initialSectionOptionsData.map((opt) =>
          opt.value === "recommended-trip" ? { ...opt, disable: true } : { ...opt, disable: false }
        );
      } else if (firstExperienceType === "experience") {
        currentSectionType = "recommended-trip";
        newSectionOptions = initialSectionOptionsData.map((opt) =>
          opt.value === "unique-experiences"
            ? { ...opt, disable: true }
            : { ...opt, disable: false }
        );
      } else {
        newSectionOptions = initialSectionOptionsData.map((opt) => ({ ...opt, disable: false }));
      }

      if (sectionType !== currentSectionType) {
        setSectionType(currentSectionType);
      }
      setSectionOptions(newSectionOptions);
    } else {
      setUniqueExperiencesList([]);

      // Enable all section options, allowing the user to select any section
      setSectionOptions(initialSectionOptionsData.map((opt) => ({ ...opt, disable: false })));
    }
  }, [uniqueExperiences, selectedDestination, selectedLocation, tabValue]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setSelectedDestination({});
    setSelectedLocation({});
    setUniqueExperiencesList([]);
    setSectionOptions(initialSectionOptionsData.map((opt) => ({ ...opt, disable: false })));
    dispatch(saveTrendData({}, true, false));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container spacing={3} pt={3}>
        <Grid size={{ xs: 12, md: "auto" }}>
          <MDBox>
            <DynamicTabs
              tabValue={tabValue}
              detailTabs={detailTabs}
              handleSetTabValue={handleTabChange}
            />
          </MDBox>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {tabValue === 0 && (
            <div style={{ marginTop: "10px", marginBottom: "10px" }}>
              <DestinationPicker
                // setFieldValue={setFieldValue}
                handleChange={handleDestinationChange}
                destination={{ label: "Destination" }}
                selectedDestination={selectedDestination}
              />
            </div>
          )}
          {tabValue === 1 && (
            <div style={{ marginTop: "10px", marginBottom: "10px" }}>
              <LocationPickerAsync
                labelObj={{ label: "Locations", name: "locations" }}
                selectedLocation={selectedLocation}
                setFieldValue={() => {}}
                handleChange={handleLocationChange}
              />
            </div>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          <DynamicRadioGroup
            groupLabel="Choose what to show in the section:"
            name="sectionType"
            value={sectionType}
            options={sectionOptions}
            setFieldValue={handleSectionChange}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {(selectedDestination?.value || selectedLocation?.value) && (
            <UniqueExperiencesList
              key={uniqueExperiencesList?.length}
              list={data}
              data={uniqueExperiencesList}
              loading={loading}
              selectedDestination={selectedDestination}
              selectedLocation={selectedLocation}
              sectionType={sectionType}
              tabValue={tabValue}
            />
          )}
        </Grid>
      </Grid>
      <Footer />
    </DashboardLayout>
  );
};

export default AllUniqueExperiences;
